import React from 'react';
import { ArrowRight, CheckCircle, Zap, BarChart, RefreshCw, Target, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../../Components/Footer';
import Header from '../../Components/Header';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';
import ShareButton from './ShareButton';


const ToolsOverview = () => {
  const tools = [
    {
      id: 'effect',
      title: '전환율 개선 효과 계산기',
      icon: '💰',
      description: '전환율 개선으로 예상되는 매출 증가와 비즈니스 임팩트를 정확히 계산합니다.',
      features: ['매출 증가 효과 예측', '개선율별 수익 계산', '월간/연간 임팩트 분석', '추가 전환수 자동 산출'],
      bgGradient: 'from-yellow-50 to-yellow-100',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      buttonGradient: 'from-yellow-600 to-yellow-700',
      path: '/tools/effect'
    },
    {
      id: 'roi',
      title: 'ROI(순수익) 계산기',
      icon: '💵',
      description: '판매가, 개당 순이익, 전환율을 입력하면 매출, ROAS, ROI, 순수익을 한번에 계산합니다. 실무에서 바로 사용할 수 있는 모든 핵심 지표를 제공합니다.',
      features: ['매출/ROAS/ROI 동시 계산', '개당 순이익 기반 분석', '월간/연간 수익 분석', '투자 효과 측정'],
      bgGradient: 'from-red-50 to-red-100',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      borderColor: 'border-red-200',
      buttonGradient: 'from-red-600 to-red-700',
      path: '/tools/roi'
    },
    {
      id: 'duration',
      title: 'A/B 테스트 기간 계산기',
      icon: '⏱️',
      description: '현재 트래픽량과 목표 개선율을 바탕으로 최적의 테스트 기간을 산출하여 성공 확률을 높입니다.',
      features: ['검증된 통계 공식', '필요 방문자 수 자동 산출', '신뢰도 수준별 계산', '트래픽별 맞춤 분석'],
      bgGradient: 'from-purple-50 to-purple-100',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      buttonGradient: 'from-purple-600 to-purple-700',
      path: '/tools/duration'
    },
    {
      id: 'significance',
      title: 'A/B 테스트 결과 판별기',
      icon: '✅',
      description: '테스트 결과가 진짜 효과인지, 단순한 우연인지 명확하게 판별해드립니다.',
      features: ['진짜 효과 vs 우연 판별', '신뢰도 수준별 검증', '승부 결과 자동 판정', '개선율 정확도 측정'],
      bgGradient: 'from-green-50 to-green-100',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200',
      buttonGradient: 'from-green-600 to-green-700',
      path: '/tools/significance'
    }
  ];

  // handleNavigation 함수 제거됨

  return (
    <div className="bg-white overflow-hidden">
      {/* 배경 장식 요소 */}
      <div className="absolute top-0 right-0 -z-10 opacity-30">
        <svg width="800" height="800" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="400" cy="400" r="400" fill="url(#radialBlue)" fillOpacity="0.3" />
          <defs>
            <radialGradient id="radialBlue" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(400)">
              <stop stopColor="#3B82F6" />
              <stop offset="1" stopColor="#3B82F6" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      <Header />

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-6">
              전환율 상승을 위한 무료 도구
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              데이터로 <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">증명하는</span><br />
              전환율 최적화 도구
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              추측이 아닌 정확한 데이터로 전환율을 높이세요.
            </p>

            {/* 실시간 스탯 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">4개</div>
                <div className="text-sm text-gray-600">필수 계산기</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">무료</div>
                <div className="text-sm text-gray-600">회원가입 불필요</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">실시간</div>
                <div className="text-sm text-gray-600">즉시 결과 확인</div>
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <ShareButton />
            </div>
          </div>
        </div>
      </section>

      {/* 도구 카드 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              전환율 최적화를 위한 필수 도구
            </h2>
            <p className="text-xl text-gray-600">
              데이터 기반의 의사결정으로 체계적인 전환율 개선을 시작하세요
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {tools.map((tool, index) => (
              <div
                key={tool.id}
                className={`bg-white rounded-2xl shadow-sm border-2 ${tool.borderColor} hover:shadow-lg transition-all duration-300 overflow-hidden group`}
              >
                <div className={`bg-gradient-to-br ${tool.bgGradient} px-8 py-8`}>
                  <div className="flex items-center">
                    <div className={`w-16 h-16 ${tool.iconBg} rounded-2xl flex items-center justify-center mr-4`}>
                      <span className="text-3xl">{tool.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {tool.title}
                      </h3>
                      <div className="inline-block px-3 py-1 bg-white/60 text-gray-700 rounded-full text-xs font-medium">
                        전환율 최적화 도구
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {tool.description}
                  </p>

                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">핵심 기능</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {tool.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link
                    to={tool.path}
                    className={`block w-full bg-gradient-to-r ${tool.buttonGradient} text-white py-4 px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center group-hover:shadow-md`}
                  >
                    <span>바로 사용하기</span>
                    <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            지금 바로 전환율 최적화를 시작하세요
          </h2>
          <p className="text-xl mb-10 text-blue-100">
            SWITCHUP으로 직관적이고 편리하게 A/B 테스트를 실행하고 전환율을 개선하세요
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-10">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { label: "직관적인 인터페이스", description: "복잡한 설정 없이 쉽게 시작" },
                { label: "30일 무료 체험", description: "부담 없이 모든 기능 사용" },
                { label: "즉시 결과 확인", description: "실시간 데이터와 분석 제공" }
              ].map((item, index) => (
                <div key={index}>
                  <div className="text-xl font-bold mb-2">{item.label}</div>
                  <div className="text-blue-100">{item.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/freestart"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              무료로 시작하기
            </Link>
            <Link
              to="/service/ab-test"
              className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 font-medium"
            >
              SWITCHUP 서비스 보기
            </Link>
          </div>
        </div>
      </section>
      {/* 카카오 문의하기 버튼 */}
      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      <Footer />
    </div>
  );
};

export default ToolsOverview;